import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { SurgeryCategory, Procedure, Surgeon, SurgeryRecord } from '@/types/surgery';

// Define the database schema
interface OfflineDBSchema extends DBSchema {
  pending_sync: {
    key: string;
    value: PendingSyncRecord;
    indexes: { 'by-created': string };
  };
  cached_data: {
    key: string;
    value: CachedData;
  };
  offline_session: {
    key: string;
    value: OfflineSession;
  };
  cached_procedures: {
    key: string;
    value: Procedure;
  };
  cached_surgeons: {
    key: string;
    value: Surgeon;
  };
  cached_records: {
    key: string;
    value: SurgeryRecord;
  };
}

// Offline session data - allows login when offline
export interface OfflineSession {
  id: string;
  userId: string;
  email: string;
  fullName?: string;
  isAdmin: boolean;
  isApproved: boolean;
  lastOnlineAt: string;
  expiresAt: string; // Session expires after 30 days offline
}

export interface PendingSyncRecord {
  id: string;
  surgeonId: string;
  surgeonName: string;
  patientName: string;
  date: string;
  procedures: Array<{
    id: string;
    code: string;
    name: string;
    value: number;
    category: SurgeryCategory;
  }>;
  createdOfflineAt: string;
  syncAttempts: number;
  lastSyncAttempt?: string;
  status: 'pending' | 'syncing' | 'failed';
}

interface CachedData {
  key: string;
  data: unknown;
  updatedAt: string;
}

const DB_NAME = 'cirurgias-offline-db';
const DB_VERSION = 2; // Bumped version for new stores

let dbInstance: IDBPDatabase<OfflineDBSchema> | null = null;

export async function getDB(): Promise<IDBPDatabase<OfflineDBSchema>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // Create pending_sync store
      if (!db.objectStoreNames.contains('pending_sync')) {
        const pendingStore = db.createObjectStore('pending_sync', { keyPath: 'id' });
        pendingStore.createIndex('by-created', 'createdOfflineAt');
      }

      // Create cached_data store for offline caching
      if (!db.objectStoreNames.contains('cached_data')) {
        db.createObjectStore('cached_data', { keyPath: 'key' });
      }

      // Create offline_session store (v2+)
      if (!db.objectStoreNames.contains('offline_session')) {
        db.createObjectStore('offline_session', { keyPath: 'id' });
      }

      // Create cached_procedures store (v2+)
      if (!db.objectStoreNames.contains('cached_procedures')) {
        db.createObjectStore('cached_procedures', { keyPath: 'id' });
      }

      // Create cached_surgeons store (v2+)
      if (!db.objectStoreNames.contains('cached_surgeons')) {
        db.createObjectStore('cached_surgeons', { keyPath: 'id' });
      }

      // Create cached_records store (v2+)
      if (!db.objectStoreNames.contains('cached_records')) {
        db.createObjectStore('cached_records', { keyPath: 'id' });
      }
    },
  });

  return dbInstance;
}

// Pending Sync Operations
export async function addPendingRecord(record: Omit<PendingSyncRecord, 'syncAttempts' | 'status'>): Promise<PendingSyncRecord> {
  const db = await getDB();
  const fullRecord: PendingSyncRecord = {
    ...record,
    syncAttempts: 0,
    status: 'pending',
  };
  await db.put('pending_sync', fullRecord);
  return fullRecord;
}

export async function getPendingRecords(): Promise<PendingSyncRecord[]> {
  const db = await getDB();
  return db.getAll('pending_sync');
}

export async function getPendingCount(): Promise<number> {
  const db = await getDB();
  return db.count('pending_sync');
}

export async function updatePendingRecord(id: string, updates: Partial<PendingSyncRecord>): Promise<void> {
  const db = await getDB();
  const record = await db.get('pending_sync', id);
  if (record) {
    await db.put('pending_sync', { ...record, ...updates });
  }
}

export async function removePendingRecord(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('pending_sync', id);
}

export async function clearAllPendingRecords(): Promise<void> {
  const db = await getDB();
  await db.clear('pending_sync');
}

// Cached Data Operations
export async function setCachedData<T>(key: string, data: T): Promise<void> {
  const db = await getDB();
  await db.put('cached_data', {
    key,
    data,
    updatedAt: new Date().toISOString(),
  });
}

export async function getCachedData<T>(key: string): Promise<T | null> {
  const db = await getDB();
  const cached = await db.get('cached_data', key);
  return cached ? (cached.data as T) : null;
}

export async function removeCachedData(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('cached_data', key);
}

// Migration from localStorage
export async function migrateFromLocalStorage(): Promise<number> {
  const OFFLINE_RECORDS_KEY = 'offline_pending_records';
  
  try {
    const stored = localStorage.getItem(OFFLINE_RECORDS_KEY);
    if (!stored) return 0;

    const oldRecords = JSON.parse(stored);
    if (!Array.isArray(oldRecords) || oldRecords.length === 0) return 0;

    const db = await getDB();
    let migrated = 0;

    for (const record of oldRecords) {
      const newRecord: PendingSyncRecord = {
        id: record.id,
        surgeonId: record.surgeonId,
        surgeonName: record.surgeonName,
        patientName: record.patientName,
        date: record.date,
        procedures: record.procedures,
        createdOfflineAt: record.createdOfflineAt || new Date().toISOString(),
        syncAttempts: 0,
        status: 'pending',
      };
      await db.put('pending_sync', newRecord);
      migrated++;
    }

    // Clear old localStorage after successful migration
    localStorage.removeItem(OFFLINE_RECORDS_KEY);
    console.log(`Migrated ${migrated} records from localStorage to IndexedDB`);
    
    return migrated;
  } catch (error) {
    console.error('Error migrating from localStorage:', error);
    return 0;
  }
}

// ====== OFFLINE SESSION OPERATIONS ======

const OFFLINE_SESSION_ID = 'current_session';
const SESSION_EXPIRY_DAYS = 30;

export async function saveOfflineSession(session: Omit<OfflineSession, 'id' | 'lastOnlineAt' | 'expiresAt'>): Promise<void> {
  const db = await getDB();
  const now = new Date();
  const expiresAt = new Date(now.getTime() + SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
  
  const fullSession: OfflineSession = {
    ...session,
    id: OFFLINE_SESSION_ID,
    lastOnlineAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };
  
  await db.put('offline_session', fullSession);
  console.log('Offline session saved for user:', session.email);
}

export async function getOfflineSession(): Promise<OfflineSession | null> {
  try {
    const db = await getDB();
    const session = await db.get('offline_session', OFFLINE_SESSION_ID);
    
    if (!session) return null;
    
    // Check if session has expired
    const now = new Date();
    const expiresAt = new Date(session.expiresAt);
    
    if (now > expiresAt) {
      console.log('Offline session expired, clearing...');
      await clearOfflineSession();
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('Error getting offline session:', error);
    return null;
  }
}

export async function updateOfflineSessionTimestamp(): Promise<void> {
  try {
    const db = await getDB();
    const session = await db.get('offline_session', OFFLINE_SESSION_ID);
    
    if (session) {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      await db.put('offline_session', {
        ...session,
        lastOnlineAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
      });
    }
  } catch (error) {
    console.error('Error updating offline session timestamp:', error);
  }
}

export async function clearOfflineSession(): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('offline_session', OFFLINE_SESSION_ID);
    console.log('Offline session cleared');
  } catch (error) {
    console.error('Error clearing offline session:', error);
  }
}

// ====== CACHED DATA OPERATIONS FOR OFFLINE ACCESS ======

export async function cacheProcedures(procedures: Procedure[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction('cached_procedures', 'readwrite');
    
    // Clear existing and add new
    await tx.store.clear();
    for (const procedure of procedures) {
      await tx.store.put(procedure);
    }
    await tx.done;
    console.log(`Cached ${procedures.length} procedures for offline use`);
  } catch (error) {
    console.error('Error caching procedures:', error);
  }
}

export async function getCachedProcedures(): Promise<Procedure[]> {
  try {
    const db = await getDB();
    return await db.getAll('cached_procedures');
  } catch (error) {
    console.error('Error getting cached procedures:', error);
    return [];
  }
}

export async function cacheSurgeons(surgeons: Surgeon[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction('cached_surgeons', 'readwrite');
    
    // Clear existing and add new
    await tx.store.clear();
    for (const surgeon of surgeons) {
      await tx.store.put(surgeon);
    }
    await tx.done;
    console.log(`Cached ${surgeons.length} surgeons for offline use`);
  } catch (error) {
    console.error('Error caching surgeons:', error);
  }
}

export async function getCachedSurgeons(): Promise<Surgeon[]> {
  try {
    const db = await getDB();
    return await db.getAll('cached_surgeons');
  } catch (error) {
    console.error('Error getting cached surgeons:', error);
    return [];
  }
}

export async function cacheRecords(records: SurgeryRecord[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction('cached_records', 'readwrite');
    
    // Clear existing and add new
    await tx.store.clear();
    for (const record of records) {
      await tx.store.put(record);
    }
    await tx.done;
    console.log(`Cached ${records.length} records for offline use`);
  } catch (error) {
    console.error('Error caching records:', error);
  }
}

export async function getCachedRecords(): Promise<SurgeryRecord[]> {
  try {
    const db = await getDB();
    return await db.getAll('cached_records');
  } catch (error) {
    console.error('Error getting cached records:', error);
    return [];
  }
}

export async function clearAllOfflineData(): Promise<void> {
  try {
    const db = await getDB();
    await Promise.all([
      db.clear('offline_session'),
      db.clear('cached_procedures'),
      db.clear('cached_surgeons'),
      db.clear('cached_records'),
      db.clear('pending_sync'),
      db.clear('cached_data'),
    ]);
    console.log('All offline data cleared');
  } catch (error) {
    console.error('Error clearing offline data:', error);
  }
}
